import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, Clock, Bell, RefreshCw, Mail, 
  Calendar, CheckCircle, XCircle, Users, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExpiringConsentsPage = () => {
  const { user } = useOutletContext();
  const [expiringConsents, setExpiringConsents] = useState([]);
  const [expiredConsents, setExpiredConsents] = useState([]);
  const [retentionPeriods, setRetentionPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thresholdDays, setThresholdDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [thresholdDays]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expiringRes, expiredRes, periodsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/consents/expiring?days=${thresholdDays}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents/expired`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents/retention-periods`, { withCredentials: true })
      ]);
      
      setExpiringConsents(expiringRes.data.consents || []);
      setExpiredConsents(expiredRes.data.consents || []);
      setRetentionPeriods(periodsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewConsent = async (consentId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/consents/${consentId}/renew`,
        { retention_period_days: 365 },
        { withCredentials: true }
      );
      toast.success('Consentimento renovado com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error renewing consent:', error);
      toast.error('Erro ao renovar consentimento');
    }
  };

  const handleSendNotifications = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/consents/notify-expiring?days=7`,
        {},
        { withCredentials: true }
      );
      toast.success(`Enviadas ${response.data.notifications_sent} notificações!`);
      fetchData();
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expiring-consents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Alertas de Consentimentos
          </h1>
          <p className="text-slate-600">
            Monitorização de consentimentos a expirar e tabela de prazos de conservação
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleSendNotifications} 
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="send-notifications-button"
          >
            <Mail className="w-4 h-4 mr-2" />
            Enviar Notificações (7 dias)
          </Button>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-700">{expiringConsents.length}</div>
                <p className="text-sm text-amber-600">A expirar em {thresholdDays} dias</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-red-700">{expiredConsents.length}</div>
                <p className="text-sm text-red-600">Expirados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-700">{retentionPeriods.length}</div>
                <p className="text-sm text-slate-600">Tipos de Prazo</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="expiring" className="w-full">
        <TabsList>
          <TabsTrigger value="expiring" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            A Expirar ({expiringConsents.length})
          </TabsTrigger>
          <TabsTrigger value="expired" className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Expirados ({expiredConsents.length})
          </TabsTrigger>
          <TabsTrigger value="periods" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Tabela de Prazos
          </TabsTrigger>
        </TabsList>

        {/* Expiring Consents Tab */}
        <TabsContent value="expiring">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    Consentimentos a Expirar
                  </CardTitle>
                  <CardDescription>
                    Consentimentos que irão expirar nos próximos dias
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="threshold" className="text-sm">Dias:</Label>
                  <Input
                    id="threshold"
                    type="number"
                    value={thresholdDays}
                    onChange={(e) => setThresholdDays(parseInt(e.target.value) || 30)}
                    className="w-20"
                    min={1}
                    max={365}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {expiringConsents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                  <p>Nenhum consentimento a expirar nos próximos {thresholdDays} dias</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titular</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Finalidades</TableHead>
                      <TableHead>Expira em</TableHead>
                      <TableHead>Dias Restantes</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiringConsents.map((consent) => (
                      <TableRow key={consent.consent_id} className={consent.days_until_expiry <= 7 ? 'bg-red-50' : consent.days_until_expiry <= 14 ? 'bg-amber-50' : ''}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{consent.user_name}</div>
                            <div className="text-xs text-slate-500">{consent.user_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{consent.contact_value}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {consent.purposes?.slice(0, 2).map(p => (
                              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                            {consent.purposes?.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{consent.purposes.length - 2}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {consent.expiration_date 
                            ? new Date(consent.expiration_date).toLocaleDateString('pt-PT')
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            consent.days_until_expiry <= 7 
                              ? 'bg-red-100 text-red-700' 
                              : consent.days_until_expiry <= 14 
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-blue-100 text-blue-700'
                          }>
                            {consent.days_until_expiry} dias
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleRenewConsent(consent.consent_id)}
                            className="bg-[#10B981] hover:bg-[#10B981]/90"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renovar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expired Consents Tab */}
        <TabsContent value="expired">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Consentimentos Expirados
              </CardTitle>
              <CardDescription>
                Consentimentos que já passaram da data de validade
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiredConsents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                  <p>Nenhum consentimento expirado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titular</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Finalidades</TableHead>
                      <TableHead>Expirou em</TableHead>
                      <TableHead>Dias Expirado</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiredConsents.map((consent) => (
                      <TableRow key={consent.consent_id} className="bg-red-50">
                        <TableCell>
                          <div>
                            <div className="font-medium">{consent.user_name}</div>
                            <div className="text-xs text-slate-500">{consent.user_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{consent.contact_value}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {consent.purposes?.slice(0, 2).map(p => (
                              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-red-600">
                          {consent.expiration_date 
                            ? new Date(consent.expiration_date).toLocaleDateString('pt-PT')
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-700">
                            {consent.days_expired} dias
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleRenewConsent(consent.consent_id)}
                            className="bg-[#10B981] hover:bg-[#10B981]/90"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renovar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retention Periods Table Tab */}
        <TabsContent value="periods">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#10B981]" />
                Tabela de Prazos de Conservação
              </CardTitle>
              <CardDescription>
                Prazos de conservação de dados conforme Art. 5º do RGPD
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Prazo</TableHead>
                    <TableHead>Base Legal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {retentionPeriods.map((period) => (
                    <TableRow key={period.id}>
                      <TableCell className="font-medium">{period.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{period.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-50">
                          {period.days 
                            ? period.days >= 365 
                              ? `${Math.floor(period.days / 365)} ano(s)` 
                              : `${period.days} dias`
                            : 'Sem prazo'
                          }
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                          {period.legal_basis}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpiringConsentsPage;
